import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Billing, Report } from "../../../features/sg/cuadre";
import type { SgPlatform } from "../../../features/sg/platforms";
import { timeAgo } from "../../../features/sg/format";
import { PlatformLogo, platformLabel } from "../components/PlatformLogo";

export const fmt = (n: number | undefined) => (n ?? 0).toLocaleString("es-MX");

// Tono por estado. Facturación y reporte llevan además texto: el color nunca va
// solo. Sin variante dark: de Tailwind (el tema es de la app, no del sistema).
export const billingTone = (b: Billing, dark: boolean) =>
  ({
    billable: dark ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" : "bg-emerald-50 text-emerald-700 ring-emerald-200",
    notBillable: dark ? "bg-amber-500/15 text-amber-300 ring-amber-500/30" : "bg-amber-50 text-amber-700 ring-amber-200",
    inactive: dark ? "bg-white/5 text-gray-400 ring-white/10" : "bg-gray-100 text-gray-600 ring-gray-200",
  })[b];

export const REPORT_DOT: Record<Report, string> = {
  ok: "bg-emerald-500",
  week: "bg-amber-500",
  old: "bg-red-500",
  none: "bg-gray-400",
};

export function Pill({ className, children, title }: { className: string; children: ReactNode; title?: string }) {
  return (
    <span title={title} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 whitespace-nowrap ${className}`}>
      {children}
    </span>
  );
}

export function BillingPill({ billing, isDarkMode }: { billing: Billing; isDarkMode: boolean }) {
  const { t } = useTranslation("sg");
  return <Pill className={billingTone(billing, isDarkMode)}>{t(`cuadre.billing.${billing}`)}</Pill>;
}

export function ReportCell({ report, ts }: { report: Report; ts: number | null }) {
  const { t } = useTranslation("sg");
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={`w-2 h-2 rounded-full shrink-0 ${REPORT_DOT[report]}`} aria-hidden="true" />
      <span>{report === "none" ? t("cuadre.report.none") : timeAgo(ts)}</span>
    </span>
  );
}

export function Logos({ list, size = 16, empty = "—" }: { list: SgPlatform[]; size?: number; empty?: string }) {
  if (!list.length) return <span className="text-gray-400 text-xs">{empty}</span>;
  return (
    <span className="inline-flex items-center gap-1.5" title={list.map(platformLabel).join(" · ")}>
      {list.map((p) => (
        <PlatformLogo key={p} platform={p} size={size} />
      ))}
    </span>
  );
}
